import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, MapPin, Layers, Map as MapIcon, Grid3x3, ExternalLink, Search } from "lucide-react";
import { useApp } from "@/context/AppContext";
import VaalMap, { MapLegend } from "@/components/vig/VaalMap";
import { Panel, StatusBadge, Tabs, Kpi } from "@/components/vig/Common";
import { OppCard, THEME_ICONS } from "@/components/vig/Cards";
import { STATUS, fmtR } from "@/lib/format";

const VIEWS = {
  map: ["municipalities", "places", "initiatives", "concept", "corridors", "parcels"],
  masterplan: ["municipalities", "concept", "initiatives", "opportunities"],
  infra: ["places", "corridors", "initiatives"],
  land: ["municipalities", "parcels", "places"],
};

const Profile = ({ init, parcelId, onOpp }) => {
  const { data, sources, themes } = useApp();
  const [tab, setTab] = useState("overview");
  useEffect(() => { setTab(parcelId ? "parcels" : "overview"); }, [init.id, parcelId]);
  const opps = data.opportunities.filter((o) => o.initiative_id === init.id);
  const parcels = data.parcels.filter((p) => p.initiative_id === init.id);
  const claims = data.claims.filter((c) => c.entity_id === init.id);
  const t = themes[init.theme];
  const img = opps[0]?.image || t?.image;
  return (
    <div data-testid="initiative-profile" className="vig-panel overflow-hidden">
      <div className="relative h-44">
        <img src={img} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06142a] to-transparent" />
        <div className="absolute right-3 top-3"><StatusBadge status={init.status} testid="initiative-status" /></div>
        <div className="absolute bottom-3 left-4 right-4">
          <h2 data-testid="initiative-name" className="text-2xl font-bold leading-tight">{init.name}</h2>
          <div className="text-[13px] text-slate-200">{init.type}</div>
        </div>
      </div>
      <div className="p-4">
        <Tabs testid="profile-tabs" value={tab} onChange={setTab} items={[["overview", "Overview"], ["components", "Components"], ["opps", `Opportunities (${opps.length})`], ...(parcels.length ? [["parcels", `Parcels (${parcels.length})`]] : []), ["sources", "Sources"]]} />
        {tab === "overview" && (
          <div className="mt-3 space-y-3 text-[13.5px] text-slate-300">
            <p>{init.description}</p>
            <div className="rounded-md border border-slate-500/20 p-3 text-[12.5px]"><b className="text-white">Sponsor / owner:</b> {init.sponsor}</div>
            <div className="rounded-md border border-amber-400/30 bg-amber-400/5 p-3 text-[12px] text-amber-200"><b>Geography:</b> {init.location_note}</div>
            {claims.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md bg-white/[0.03] px-3 py-2 text-[12.5px]"><span>{c.statement}: <b className="text-white">{c.value}</b> <span className="text-slate-500">[{c.source_id}]</span></span><StatusBadge status={c.status} /></div>
            ))}
          </div>
        )}
        {tab === "components" && (
          <div className="mt-3 grid grid-cols-2 gap-2">{init.components.map((c) => <div key={c} className="rounded-md border border-slate-500/20 px-3 py-2.5 text-[12.5px]">{c}</div>)}</div>
        )}
        {tab === "opps" && (
          <div className="mt-3 space-y-2">
            {opps.length === 0 && <p className="text-[13px] text-slate-400">No illustrative opportunity records linked. Shown for regional context only — not marketed as investable.</p>}
            {opps.map((o) => (
              <button key={o.id} data-testid={`profile-opp-${o.id}`} onClick={() => onOpp(o.id)} className="vig-row flex w-full items-center justify-between rounded-md border border-slate-500/20 px-3 py-2 text-left">
                <span><span className="block text-[13px] font-semibold">{o.name}</span><span className="text-[11px] text-slate-400">{o.phase} · {fmtR(o.capex_m)} synthetic · {o.area_ha} ha</span></span><ArrowRight className="h-4 w-4 text-[#F4B842]" />
              </button>
            ))}
          </div>
        )}
        {tab === "parcels" && (
          <div className="mt-3 space-y-2" data-testid="parcel-list">
            {parcels.map((p) => (
              <div key={p.id} data-testid={`parcel-${p.id}`} className={`rounded-md border px-3 py-2 text-[12.5px] ${p.id === parcelId ? "border-[#F4B842] bg-[#F4B842]/5" : "border-slate-500/20"}`}>
                <div className="flex justify-between"><b className="text-white">{p.name}</b><span>{p.area_ha.toLocaleString()} ha</span></div>
                <div className="text-slate-400">{p.legal}</div>
                <div className="text-slate-400">Land rights: {p.land_rights} · Zoning: {p.zoning}</div>
              </div>
            ))}
            <p className="text-[11px] text-amber-200">Parcel schedule per Gazette 54251 [S8]. Map markers are approximate localities — not surveyed or cadastral boundaries. Schedule sums to 3,080 ha versus 817 ha cited in the dtic statement [S2]; reconciliation outstanding.</p>
          </div>
        )}
        {tab === "sources" && (
          <div className="mt-3 space-y-2">
            {init.sources.map((s) => sources[s] && (
              <a key={s} href={sources[s].url || undefined} target="_blank" rel="noreferrer" className="vig-row block rounded-md border border-slate-500/20 px-3 py-2 text-[12.5px]">
                <div className="flex items-center justify-between"><b className="text-white">[{s}] {sources[s].title}</b>{sources[s].url && <ExternalLink className="h-3.5 w-3.5" />}</div>
                <div className="text-slate-400">{sources[s].publisher} · {sources[s].date}</div>
                <div className="text-slate-400">{sources[s].scope}</div>
              </a>
            ))}
            <div className="text-[11px] text-slate-500">As of {init.as_of}</div>
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid="profile-evaluate-btn" disabled={!opps.length} onClick={() => onOpp(opps[0]?.id)} className="vig-btn-gold flex items-center justify-center gap-2 px-3 py-2.5 text-[13px]">Evaluate Opportunity <ArrowRight className="h-4 w-4" /></button>
          <button data-testid="profile-validate-btn" disabled={!opps.length} onClick={() => onOpp(opps[0]?.id, "/validate")} className="vig-btn-ghost px-3 py-2.5 text-[13px] disabled:opacity-40">View Evidence</button>
        </div>
      </div>
    </div>
  );
};

export default function Explore() {
  const { data, initiatives, setSelectedOpp, themes } = useApp();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const initId = sp.get("initiative") || "sez";
  const parcelId = sp.get("parcel");
  const [view, setView] = useState("map");
  const [sector, setSector] = useState(sp.get("theme") || "all");
  const [panelTab, setPanelTab] = useState("sectors");
  const [statuses, setStatuses] = useState(Object.keys(STATUS));
  const [q, setQ] = useState("");
  const init = initiatives[initId] || data.initiatives[0];
  const parcel = data.parcels.find((p) => p.id === parcelId);
  const fly = useMemo(() => (parcel ? { lat: parcel.lat, lng: parcel.lng, zoom: 12 } : { lat: init.lat, lng: init.lng, zoom: 11 }), [init, parcel]);
  const featured = data.opportunities.filter((o) => (sector === "all" || o.theme === sector) && o.name.toLowerCase().includes(q.toLowerCase()));
  const select = (k, id) => {
    if (k === "initiative") setSp({ initiative: id });
    else if (k === "parcel") setSp({ initiative: "sez", parcel: id });
    else { const o = data.opportunities.find((x) => x.id === id); setSp({ initiative: o.initiative_id, opp: id }); setSelectedOpp(id); }
  };
  const openOpp = (id, route = "/evaluate") => { if (!id) return; setSelectedOpp(id); nav(`${route}?opp=${id}`); };
  const counts = (t) => data.initiatives.filter((i) => i.theme === t).length + data.opportunities.filter((o) => o.theme === t).length;

  return (
    <main data-testid="explore-page" className="relative">
      <div className="absolute inset-x-0 top-0 h-[300px] overflow-hidden"><img src="/media/hero.jpg" alt="" className="h-full w-full object-cover opacity-40" /><div className="absolute inset-0 bg-gradient-to-b from-[#06142a]/60 to-[#06142a]" /></div>
      <div className="relative mx-auto grid max-w-[1600px] gap-5 px-5 pt-24 lg:px-8 xl:grid-cols-[330px_1fr_400px]">
        <div className="space-y-4">
          <div>
            <div className="vig-eyebrow">02 | EXPLORE</div>
            <h1 className="mt-1 text-4xl font-bold leading-tight sm:text-5xl xl:text-[40px]" data-testid="page-title">The Opportunity Landscape.</h1>
            <p className="mt-2 text-[15px] text-slate-200">Explore development areas, infrastructure and investment opportunities across the Vaal region.</p>
            <p className="mt-2 text-[13px] text-slate-400">Click an initiative, parcel or opportunity to open its profile, status and sources.</p>
          </div>
          <div className="grid grid-cols-2 gap-0 vig-glass">
            <Kpi icon={MapPin} value={data.initiatives.length} label="Distinct initiatives" />
            <Kpi icon={Layers} value={data.opportunities.length} label="Illustrative opportunities" />
            <Kpi icon={MapIcon} value="5" label="Gazetted SEZ parcels" sub="[S8]" />
            <Kpi icon={Grid3x3} value={data.themes.length} label="Key sectors" />
          </div>
          <Panel testid="explore-filters" bodyClass="p-3">
            <Tabs testid="explore-filter-tabs" value={panelTab} onChange={setPanelTab} items={[["sectors", "Sectors"], ["status", "Status"]]} />
            {panelTab === "sectors" && (
              <div className="mt-2 space-y-0.5">
                {[{ id: "all", name: "All Sectors" }, ...data.themes].map((t) => {
                  const I = THEME_ICONS[t.id] || Grid3x3;
                  return (
                    <button key={t.id} data-testid={`sector-filter-${t.id}`} onClick={() => setSector(t.id)} className={`flex w-full items-center justify-between rounded px-2.5 py-2 text-[13px] ${sector === t.id ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5"}`}>
                      <span className="flex items-center gap-2"><I className="h-4 w-4" style={{ color: themes[t.id]?.color }} />{t.name}</span>
                      <span className="text-[11px] text-slate-400">{t.id === "all" ? data.initiatives.length + data.opportunities.length : counts(t.id)}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {panelTab === "status" && (
              <div className="mt-2 space-y-1">
                {Object.entries(STATUS).map(([k, s]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px] hover:bg-white/5">
                    <input data-testid={`status-filter-${k}`} type="checkbox" checked={statuses.includes(k)} onChange={() => setStatuses(statuses.includes(k) ? statuses.filter((x) => x !== k) : [...statuses, k])} className="accent-[#F4B842]" />
                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />{s.label}
                  </label>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <Tabs testid="explore-view-tabs" value={view} onChange={setView} items={[["map", "Map View"], ["masterplan", "Conceptual Zones"], ["infra", "Infrastructure"], ["land", "Land / Parcels"]]} />
            <div className="relative w-56"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><input data-testid="explore-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter opportunities…" className="vig-input pl-8" /></div>
          </div>
          <div className="relative">
            <VaalMap height={640} layers={VIEWS[view]} theme={sector === "all" ? null : sector} statusFilter={statuses} selectedId={parcelId || sp.get("opp") || init.id} onSelect={select} flyTo={fly} testid="explore-map" />
            <div className="absolute right-2 top-2 z-[500] w-44"><MapLegend /></div>
          </div>
        </div>

        <Profile init={init} parcelId={parcelId} onOpp={openOpp} />
      </div>

      <section className="mx-auto mt-8 max-w-[1600px] px-5 lg:px-8">
        <div className="flex items-end justify-between">
          <h2 className="text-xl font-semibold">Featured Development Opportunities <span className="text-sm font-normal text-purple-300">(illustrative)</span></h2>
          <button data-testid="explore-view-all-btn" onClick={() => setSector("all")} className="flex items-center gap-1 text-[13px] font-semibold text-[#F4B842]">View all opportunities <ArrowRight className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5" data-testid="explore-featured">
          {featured.map((o) => <OppCard key={o.id} opp={o} active={sp.get("opp") === o.id} onClick={() => select("opportunity", o.id)} cta="Open on map · Evaluate →" />)}
        </div>
        {sp.get("opp") && (
          <div className="mt-4 flex justify-end">
            <button data-testid="explore-evaluate-selected-btn" onClick={() => openOpp(sp.get("opp"))} className="vig-btn-gold flex items-center gap-2 px-5 py-2.5 text-sm">Evaluate {data.opportunities.find((o) => o.id === sp.get("opp"))?.name} <ArrowRight className="h-4 w-4" /></button>
          </div>
        )}
      </section>
    </main>
  );
}
